import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { AdviceApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/advice/advice-api';
import { CreateAdviceResponseRequest } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/advice/create-advice-response.request';
import { StorageService } from '../front/storage.service';
import { loadErrorKind } from './load-state';

/** Les réponses parties sans réseau, rejouées au retour. */
export const ADVICE_REPLY_QUEUE_KEY = 'advice-reply-queue';
/** Le serveur accepte une réponse jusqu'à 7 jours en arrière. */
const MAX_AGE_DAYS = 7;

/** Une réponse envoyée : son `uid` pour l'annuler, ou sa place dans la file sans réseau. */
export interface SentReply {
  uid: string | null;
  /** Gardée sur l'appareil, envoyée au retour du réseau. */
  queuedId: string | null;
}

interface QueuedReply extends CreateAdviceResponseRequest {
  id: string;
  day: string;
}

/**
 * Répondre à l'assistant (« C'est fait », « Déjà fait ») et l'annuler.
 *
 * Sans réseau, la réponse est gardée sur l'appareil avec son jour, et envoyée
 * au retour du réseau (ou à la prochaine ouverture) : le message part tout de
 * suite à l'écran, la réponse suit. « Annuler » la retire de la file si elle
 * n'est pas encore partie. Une autre panne (serveur, refus) se dit à l'écran.
 */
@Injectable({ providedIn: 'root' })
export class AdviceReplyStore implements OnDestroy {
  // Les classes du SDK lisent leur configuration dans un singleton, comme `GardenTodoStore`.
  private readonly api = new AdviceApi();
  private readonly storage = inject(StorageService);
  private readonly zone = inject(NgZone);
  private flushing: Promise<void> | null = null;
  /** Les réponses de la file parties au retour du réseau : de quoi les annuler après coup. */
  private readonly sentFromQueue = new Map<string, string>();
  private readonly onOnline = () => void this.flush();

  constructor() {
    this.zone.runOutsideAngular(() => window.addEventListener('online', this.onOnline));
  }

  ngOnDestroy(): void {
    window.removeEventListener('online', this.onOnline);
  }

  async reply(request: CreateAdviceResponseRequest): Promise<SentReply> {
    const day = request.day ?? new Date().toLocaleDateString('en-CA');
    try {
      const response = await this.api.reply({ ...request, day });
      return { uid: response.uid, queuedId: null };
    } catch (error) {
      if (loadErrorKind(error) !== 'network') throw error;
      const queued: QueuedReply = { ...request, day, id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` };
      this.write([...this.queue(), queued]);
      return { uid: null, queuedId: queued.id };
    }
  }

  /**
   * « Annuler » : la réponse envoyée est retirée ; celle qui attendait le
   * réseau ne part pas, ou, partie entre-temps, est retirée à son tour.
   */
  async cancel(sent: SentReply): Promise<void> {
    if (sent.queuedId) {
      if (this.queue().some((q) => q.id === sent.queuedId)) {
        this.remove(sent.queuedId);
        return;
      }
      const uid = this.sentFromQueue.get(sent.queuedId);
      this.sentFromQueue.delete(sent.queuedId);
      if (uid) await this.api.cancelReply(uid);
      return;
    }
    if (sent.uid) await this.api.cancelReply(sent.uid);
  }

  /** Envoie ce qui attendait, dans l'ordre ; s'arrête à la première coupure. Trop vieux pour le serveur : oublié. */
  flush(): Promise<void> {
    this.flushing ??= this.send().finally(() => (this.flushing = null));
    return this.flushing;
  }

  /** Réponses en attente de réseau. */
  pending(): number {
    return this.queue().length;
  }

  /** Les réponses de ce jour qui attendent le réseau : le mot du jour les compte comme données. */
  queuedFor(day: string): { triggerCode: string; plantUid?: string; day: string }[] {
    return this.queue()
      .filter((q) => q.day === day)
      .map(({ triggerCode, plantUid }) => ({ triggerCode, plantUid, day }));
  }

  private async send(): Promise<void> {
    const oldest = new Date();
    oldest.setDate(oldest.getDate() - MAX_AGE_DAYS);
    const limit = oldest.toLocaleDateString('en-CA');
    for (const item of this.queue()) {
      if (item.day < limit) {
        this.remove(item.id);
        continue;
      }
      // Annulée pendant l'envoi des précédentes : elle ne part pas.
      if (!this.queue().some((q) => q.id === item.id)) continue;
      const { id, ...request } = item;
      try {
        const response = await this.api.reply(request);
        this.sentFromQueue.set(id, response.uid);
        this.remove(id);
      } catch (error) {
        if (loadErrorKind(error) === 'network') return;
        // Refusée (déjà répondue, plante partie) : elle ne repartira pas mieux.
        this.remove(id);
      }
    }
  }

  private remove(id: string): void {
    this.write(this.queue().filter((q) => q.id !== id));
  }

  private queue(): QueuedReply[] {
    try {
      const parsed = JSON.parse(this.storage.getItem(ADVICE_REPLY_QUEUE_KEY) ?? '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private write(queue: QueuedReply[]): void {
    try {
      if (queue.length) this.storage.setItem(ADVICE_REPLY_QUEUE_KEY, JSON.stringify(queue));
      else this.storage.removeItem(ADVICE_REPLY_QUEUE_KEY);
    } catch {
      // Stockage refusé : la réponse hors ligne est perdue, comme sans file.
    }
  }
}
